import { Link } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/composer.pb';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useMediaQuery } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Button, Text } from '@frontend/design-system';
import { ButtonLinkType } from '../../../types';
import { useContentComposer } from '../../content-composer-provider';
import { PopoverButtonGroup, ToolbarButtonGroup } from './button-group';
import { ExpansionButton } from './expansion-button';
import { iconStyling, toolbarStyling } from './styles';

const CALL_TO_ACTION_LINK = '{{CALL_TO_ACTION_LINK}}';
const PRACTICE_PHONE = '{{BUSINESS_GROUP_PHONE}}';
const PRACTICE_ADDRESS = '{{BUSINESS_GROUP_ADDRESS}}';
const SCHEDULE_LINK = '{{SCHEDULE_LINK}}';
const EXTRA_LARGE_BREAKPOINT = 1500;
const LARGE_BREAKPOINT = 1320;

export type Option = { linkType: ButtonLinkType; label: string; url?: string };

type LinkButtonProps = {
  link?: Link;
  onClick: (link: Link) => void;
  openModal: () => void;
  option: Option;
  setShowCustom: (show: boolean) => void;
};

const LinkOptionButton = ({ link, onClick, openModal, option, setShowCustom }: LinkButtonProps) => (
  <Button
    variant='secondary'
    key={option.linkType}
    css={{
      padding: theme.spacing(1),
      justifyContent: 'flex-start',
      width: 'auto',
      fontWeight: theme.font.weight.regular,
    }}
    onClick={() => {
      const newLink: Link = { ...link, linkType: '', url: '' };
      if (option.linkType === 'form') {
        openModal();
      } else if (
        option.linkType === 'address' ||
        option.linkType === 'schedule' ||
        option.linkType === 'phone' ||
        option.linkType === 'confirmation'
      ) {
        newLink.url = option.url ?? '';
      } else if (option.linkType === 'custom') {
        setShowCustom(true);
      }
      newLink.linkType = option.linkType;
      onClick(newLink);
    }}
  >
    <Icon name={option.linkType === 'form' ? 'forms' : 'link'} css={iconStyling} />
    <Text as='span'>{option.label}</Text>
  </Button>
);

type Props = {
  link?: Link;
  onClick: (link: Link) => void;
  openModal: () => void;
  setShow?: (show: boolean) => void;
  setShowCustom: (show: boolean) => void;
};

export const LinkOptionsToolbar = ({ setShow, ...rest }: Props) => {
  const { t } = useTranslation('content-composer');
  const { composerMode } = useContentComposer();

  const options: Option[] = [
    { linkType: 'phone', label: t('Practice Phone'), url: PRACTICE_PHONE },
    { linkType: 'address', label: t('Practice Address'), url: PRACTICE_ADDRESS },
    { linkType: 'schedule', label: t('Schedule'), url: SCHEDULE_LINK },
    { linkType: 'form', label: t('Form') },
    composerMode === 'auto-message'
      ? { linkType: 'confirmation', label: t('Confirmation'), url: CALL_TO_ACTION_LINK }
      : undefined,
    { linkType: 'custom', label: t('Custom') },
  ].filter((option): option is NonNullable<Option> => !!option);

  const alwaysPresentOptions = options.slice(0, 1);
  const mediumScreenSizeOptions = options
    .filter((option) => !alwaysPresentOptions.find((o) => o.linkType === option.linkType))
    .slice(0, 2);
  const largeScreenSizeOptions = options.filter(
    (option) =>
      !alwaysPresentOptions.find((o) => o.linkType === option.linkType) &&
      !mediumScreenSizeOptions.find((o) => o.linkType === option.linkType)
  );
  const isLarge = useMediaQuery(`(min-width: ${LARGE_BREAKPOINT}px) and (max-width: ${EXTRA_LARGE_BREAKPOINT}px)`);

  return (
    <div css={toolbarStyling}>
      {setShow && <Icon name='x' onClick={() => setShow(false)} css={iconStyling} />}
      {alwaysPresentOptions.map((option) => (
        <LinkOptionButton key={option.linkType} option={option} {...rest} />
      ))}
      <ToolbarButtonGroup breakpoint={LARGE_BREAKPOINT}>
        {mediumScreenSizeOptions.map((option) => (
          <LinkOptionButton key={option.linkType} option={option} {...rest} />
        ))}
      </ToolbarButtonGroup>
      <ToolbarButtonGroup breakpoint={EXTRA_LARGE_BREAKPOINT}>
        {largeScreenSizeOptions.map((option) => (
          <LinkOptionButton key={option.linkType} option={option} {...rest} />
        ))}
      </ToolbarButtonGroup>
      {((mediumScreenSizeOptions.length > 0 && !isLarge) || largeScreenSizeOptions.length > 0) && (
        <ExpansionButton breakpoint={EXTRA_LARGE_BREAKPOINT} hideBorder>
          {mediumScreenSizeOptions.length > 0 ? (
            <PopoverButtonGroup breakpoint={LARGE_BREAKPOINT}>
              {mediumScreenSizeOptions.map((option) => (
                <LinkOptionButton key={option.linkType} option={option} {...rest} />
              ))}
            </PopoverButtonGroup>
          ) : null}
          {largeScreenSizeOptions.length > 0
            ? largeScreenSizeOptions.map((option) => (
                <LinkOptionButton key={option.linkType} option={option} {...rest} />
              ))
            : null}
        </ExpansionButton>
      )}
    </div>
  );
};
